import React, {useEffect, useId, useMemo, useRef, useState} from "react";
import {Overlay} from "react-bootstrap";
import {PiCalendarBlank, PiCaretLeft, PiCaretRight} from "react-icons/pi";
import {formatDateDE, toISODate} from "./format.js";

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function parseISO(iso) {
    if (!iso) {
        return null;
    }
    const [y, m, d] = String(iso).split("-").map(Number);
    if (!y || !m || !d) {
        return null;
    }
    return {y, m, d};
}

function daysInMonth(y, m) {
    return new Date(y, m, 0).getDate();
}

// Montag = 0 ... Sonntag = 6 (JS liefert 0 = Sonntag).
function weekdayIndex(y, m, d) {
    return (new Date(y, m - 1, d).getDay() + 6) % 7;
}

function buildMonthGrid(y, m) {
    const total = daysInMonth(y, m);
    const firstOffset = weekdayIndex(y, m, 1);
    const cells = [];
    for (let i = 0; i < firstOffset; i += 1) {
        cells.push(null);
    }
    for (let day = 1; day <= total; day += 1) {
        cells.push(day);
    }
    while (cells.length % 7 !== 0) {
        cells.push(null);
    }
    return cells;
}

function clampMonth(y, m) {
    if (m < 1) {
        return {y: y - 1, m: 12};
    }
    if (m > 12) {
        return {y: y + 1, m: 1};
    }
    return {y, m};
}

/**
 * Eigens gestaltetes Datumsfeld als Ersatz für <input type="date"> - siehe
 * globale Vorgabe "keine browsereigenen Bedienelemente". Ein Klick öffnet
 * einen einmonatigen Kalender (Überlagerung wie bei SelectList); Tage
 * außerhalb von min/max sind deaktiviert. Escape schließt, Klick nach außen
 * ebenfalls (react-bootstrap Overlay/rootClose).
 */
export default function DatePickerField({id, label, value, onChange, min, max, size, disabled = false, className = "", style}) {
    const generatedId = useId();
    const instanceId = id || `date-field-${generatedId}`;
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);

    const selected = useMemo(() => parseISO(value), [value]);
    const minParsed = useMemo(() => parseISO(min), [min]);
    const maxParsed = useMemo(() => parseISO(max), [max]);

    const fallback = selected || minParsed || {y: new Date().getFullYear(), m: new Date().getMonth() + 1};
    const [viewYear, setViewYear] = useState(fallback.y);
    const [viewMonth, setViewMonth] = useState(fallback.m);

    useEffect(() => {
        if (!open) {
            return;
        }
        const base = selected || minParsed || {y: new Date().getFullYear(), m: new Date().getMonth() + 1};
        setViewYear(base.y);
        setViewMonth(base.m);
        // Nur beim Öffnen auf den aktuellen Wert springen - während der
        // Bedienung soll die Monatsnavigation nicht zurückspringen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const isDisabledDate = (y, m, d) => {
        const iso = toISODate(y, m, d);
        if (minParsed && iso < toISODate(minParsed.y, minParsed.m, minParsed.d)) {
            return true;
        }
        if (maxParsed && iso > toISODate(maxParsed.y, maxParsed.m, maxParsed.d)) {
            return true;
        }
        return false;
    };

    const canGoPrevMonth = !minParsed || viewYear > minParsed.y || (viewYear === minParsed.y && viewMonth > minParsed.m);
    const canGoNextMonth = !maxParsed || viewYear < maxParsed.y || (viewYear === maxParsed.y && viewMonth < maxParsed.m);

    const goMonth = (delta) => {
        const next = clampMonth(viewYear, viewMonth + delta);
        setViewYear(next.y);
        setViewMonth(next.m);
    };

    const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);
    const monthLabel = new Date(viewYear, viewMonth - 1, 1).toLocaleDateString("de-DE", {month: "long", year: "numeric"});

    const closeAndFocus = () => {
        setOpen(false);
        triggerRef.current?.focus();
    };

    const selectDay = (day) => {
        if (!day || isDisabledDate(viewYear, viewMonth, day)) {
            return;
        }
        onChange(toISODate(viewYear, viewMonth, day));
        closeAndFocus();
    };

    const handleTriggerKeyDown = (event) => {
        if (disabled) {
            return;
        }
        if (!open && (event.key === "Enter" || event.key === " " || event.key === "ArrowDown")) {
            event.preventDefault();
            setOpen(true);
        }
    };

    const handlePanelKeyDown = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            closeAndFocus();
        }
    };

    const sizeClass = size === "sm" ? " form-control-sm" : "";

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                id={instanceId}
                className={`form-control text-start d-inline-flex align-items-center justify-content-between gap-2${sizeClass}${className ? ` ${className}` : ""}`}
                style={{cursor: disabled ? "default" : "pointer", ...style}}
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={label}
                onClick={() => setOpen((current) => !current)}
                onKeyDown={handleTriggerKeyDown}
            >
                <span className={value ? "" : "text-muted"}>{value ? formatDateDE(value) : "TT.MM.JJJJ"}</span>
                <PiCalendarBlank className="text-muted flex-shrink-0" aria-hidden="true" />
            </button>

            <Overlay target={triggerRef} show={open} placement="bottom-start" flip rootClose onHide={() => setOpen(false)}>
                {(overlayProps) => (
                    <div
                        {...overlayProps}
                        role="dialog"
                        aria-label={label}
                        className="bg-white border rounded-2 shadow-sm p-2"
                        style={{...overlayProps.style, width: 272, zIndex: 3000}}
                        onKeyDown={handlePanelKeyDown}
                    >
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <button type="button" className="btn btn-sm btn-light" disabled={!canGoPrevMonth} onClick={() => goMonth(-1)} aria-label="Vorheriger Monat">
                                <PiCaretLeft />
                            </button>
                            <span className="small fw-semibold text-capitalize">{monthLabel}</span>
                            <button type="button" className="btn btn-sm btn-light" disabled={!canGoNextMonth} onClick={() => goMonth(1)} aria-label="Nächster Monat">
                                <PiCaretRight />
                            </button>
                        </div>
                        <div className="d-grid" style={{gridTemplateColumns: "repeat(7, 1fr)", gap: 2}}>
                            {WEEKDAY_LABELS.map((weekday) => (
                                <div key={weekday} className="text-center text-muted" style={{fontSize: "0.7rem"}}>
                                    {weekday}
                                </div>
                            ))}
                            {grid.map((day, index) => {
                                if (!day) {
                                    return <div key={`empty-${index}`} />;
                                }
                                const isSelected = !!selected && selected.y === viewYear && selected.m === viewMonth && selected.d === day;
                                const isDisabledDay = isDisabledDate(viewYear, viewMonth, day);
                                return (
                                    <button
                                        type="button"
                                        key={day}
                                        onClick={() => selectDay(day)}
                                        disabled={isDisabledDay}
                                        aria-pressed={isSelected}
                                        className={`btn btn-sm rounded-1 ${isSelected ? "btn-primary" : "btn-light"}`}
                                        style={{padding: "0.25rem 0", fontSize: "0.8rem"}}
                                    >
                                        {day}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </Overlay>
        </>
    );
}
